import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { ICaremapAccess } from '../../caremap/ICareMap';
import { CommunityModelFactory } from '../CommunityModelFactory';
import { MixPanelEventEmitter as MixpanelService } from '../../metrics/MixPanelEventEmitter';
import { accessRemovedEvents, invitationCancelledTypes } from '../accessLevels';

export interface IMemberAccessLink {
    access: ICaremapAccess;
    name: string;
    isPending: boolean;
    token: string;
}

// Bridges a care-map member's delete flow to its Sharing & Access link: resolves whether the
// member being deleted also holds access, and revokes it (with the same tracking the Sharing &
// Access list fires) when the deleter opts in. Keeps the access-removal event mapping in one
// place so both the household and support-group delete hooks stay consistent.
export const useMemberAccessRevocation = () => {
    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);

    const tracking = useInstance(MixpanelService);

    const findAccessLink = (member: { memberId: string | undefined; email?: string; linkedUserId?: string }): IMemberAccessLink | undefined => {
        const community = factory.forActiveCaremap();
        if (!community.isLoaded()) {
            return undefined;
        }
        return community.findCollaboratorForMember(member);
    };

    const revokeAccess = (link: IMemberAccessLink) => {
        const community = factory.forActiveCaremap();
        if (link.isPending) {
            community.deleteInvite(link.token);
            tracking.track('Invitation Cancelled', () => ({ type: invitationCancelledTypes[link.access] }));
        } else {
            community.deleteCollaborator(link.token);
            tracking.track(accessRemovedEvents[link.access]);
        }
    };

    return { findAccessLink, revokeAccess };
};
